import { useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addDaysIso,
  CAMPAIGN_DATE_MAX_DAYS,
  todayIsoDate,
  tomorrowIsoDate,
  validateCampaignDateRange,
} from '@/features/campaigns/utils/campaignUtils';
import { FieldError } from '@/features/campaigns/components/FormPrimitives';
import { FloatingOverlay } from '@/shared/ui/FloatingOverlay';
import { MbHelpTooltip } from './primitives';
import { useInstantAdAccess } from '@/features/profile/hooks/useInstantAdAccess';

type DateRangePickerProps = {
  startDate: string;
  endDate: string;
  onChange: (next: { startDate: string; endDate: string }) => void;
  errors?: Record<string, string>;
  clearError?: (key: string) => void;
  allowToday?: boolean;
};

type FocusEdge = 'start' | 'end';

function parseIso(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function toIso(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function formatLong(iso: string) {
  if (!iso) return 'Add date';
  return parseIso(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function dayCount(start: string, end: string) {
  if (!start || !end) return 0;
  const a = parseIso(start).getTime();
  const b = parseIso(end).getTime();
  return Math.round((b - a) / 86400000) + 1;
}

function buildMonthCells(month: Date) {
  const first = startOfMonth(month);
  const startPad = first.getDay();
  const daysInMonth = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0,
  ).getDate();
  const out: Array<{ iso: string } | null> = [];
  for (let i = 0; i < startPad; i++) out.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    out.push({
      iso: toIso(new Date(month.getFullYear(), month.getMonth(), day)),
    });
  }
  while (out.length % 7 !== 0) out.push(null);
  return out;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const PRESETS = [
  { id: '7', label: '7 days', days: 7 },
  { id: '14', label: '14 days', days: 14 },
  { id: '30', label: '30 days', days: 30 },
] as const;

/**
 * Dual-month date-range picker shown in a dimmed overlay sheet
 * so it never collides with the form layout behind it.
 */
export function ManualDateRangePicker({
  startDate,
  endDate,
  onChange,
  errors = {},
  clearError,
  allowToday = false,
}: DateRangePickerProps) {
  const instantAdAccess = useInstantAdAccess();
  const minStart =
    allowToday || instantAdAccess ? todayIsoDate() : tomorrowIsoDate();
  const maxEndCap = addDaysIso(minStart, CAMPAIGN_DATE_MAX_DAYS);

  const [open, setOpen] = useState(false);
  const [focusEdge, setFocusEdge] = useState<FocusEdge>('start');
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [cursor, setCursor] = useState(() =>
    startOfMonth(parseIso(startDate || minStart)),
  );
  const rootRef = useRef<HTMLDivElement>(null);

  const liveErrors = validateCampaignDateRange(startDate, endDate, minStart);
  const startError =
    errors.startDate || (startDate ? liveErrors.startDate : '') || '';
  const endError =
    errors.endDate || (endDate ? liveErrors.endDate : '') || '';

  const monthLeft = cursor;
  const monthRight = addMonths(cursor, 1);
  const cellsLeft = useMemo(() => buildMonthCells(monthLeft), [monthLeft]);
  const cellsRight = useMemo(() => buildMonthCells(monthRight), [monthRight]);

  const previewEnd =
    startDate && !endDate && hoverDate && hoverDate >= startDate
      ? hoverDate
      : endDate;

  const nights = dayCount(startDate, endDate);
  const isDisabled = (iso: string) => iso < minStart || iso > maxEndCap;

  const openPicker = (edge: FocusEdge) => {
    setFocusEdge(edge);
    setOpen(true);
    const anchor = edge === 'end' && endDate ? endDate : startDate || minStart;
    setCursor(startOfMonth(parseIso(anchor)));
  };

  const closePicker = () => setOpen(false);

  const applyPreset = (days: number) => {
    clearError?.('startDate');
    clearError?.('endDate');
    const end = addDaysIso(minStart, days - 1);
    onChange({ startDate: minStart, endDate: end });
    setFocusEdge('end');
    setCursor(startOfMonth(parseIso(minStart)));
    setOpen(true);
  };

  const pick = (iso: string) => {
    if (isDisabled(iso)) return;
    clearError?.('startDate');
    clearError?.('endDate');

    if (focusEdge === 'end' && startDate) {
      if (iso < startDate) {
        onChange({ startDate: iso, endDate: '' });
        setFocusEdge('end');
        return;
      }
      const maxForStart = addDaysIso(startDate, CAMPAIGN_DATE_MAX_DAYS);
      if (iso > maxForStart) return;
      onChange({ startDate, endDate: iso });
      return;
    }

    if (!startDate || (startDate && endDate)) {
      onChange({ startDate: iso, endDate: '' });
      setFocusEdge('end');
      return;
    }
    if (iso < startDate) {
      onChange({ startDate: iso, endDate: '' });
      setFocusEdge('end');
      return;
    }
    const maxForStart = addDaysIso(startDate, CAMPAIGN_DATE_MAX_DAYS);
    if (iso > maxForStart) return;
    onChange({ startDate, endDate: iso });
    setFocusEdge('end');
  };

  const overlayHint =
    startDate && !endDate
      ? 'Select an end date'
      : focusEdge === 'end'
        ? 'Adjust end date'
        : 'Select a start date';

  const renderMonth = (
    month: Date,
    cells: Array<{ iso: string } | null>,
    key: string,
  ) => (
    <div key={key} className="mb-cal-inline__month">
      <p className="mb-cal-inline__month-label">
        {month.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
        })}
      </p>
      <div className="mb-cal-inline__weekdays">
        {WEEKDAYS.map((d, i) => (
          <div key={`${key}-${d}-${i}`} className="mb-cal-inline__weekday">
            {d}
          </div>
        ))}
      </div>
      <div
        className="mb-cal-inline__grid"
        onMouseLeave={() => setHoverDate(null)}
      >
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={`${key}-e-${idx}`} className="mb-cal-inline__cell" />;
          }
          const { iso } = cell;
          const disabled = isDisabled(iso);
          const isStart = iso === startDate;
          const isEnd = iso === endDate;
          const inRange =
            !!startDate &&
            !!previewEnd &&
            iso >= startDate &&
            iso <= previewEnd;
          const isPreviewEdge =
            !!startDate &&
            !endDate &&
            !!hoverDate &&
            iso === hoverDate &&
            hoverDate >= startDate;
          const isEdge = isStart || isEnd || isPreviewEdge;
          const isToday = iso === todayIsoDate();

          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              aria-label={iso}
              aria-pressed={isStart || isEnd}
              onMouseEnter={() => setHoverDate(iso)}
              onClick={() => pick(iso)}
              className={[
                'mb-cal-inline__day',
                inRange && !isEdge ? 'is-in-range' : '',
                isEdge ? 'is-edge' : '',
                isStart && (endDate || previewEnd) ? 'is-range-start' : '',
                (isEnd || isPreviewEdge) && startDate ? 'is-range-end' : '',
                isToday && !isEdge && !inRange ? 'is-today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="mb-cal-inline__day-num">
                {Number(iso.slice(-2))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const activePreset = PRESETS.find(
    (p) =>
      startDate === minStart &&
      endDate === addDaysIso(minStart, p.days - 1),
  )?.id;

  return (
    <div ref={rootRef} className="mb-field mb-cal-range">
      <div className="mb-field__label-row">
        <span className="mb-field-label">
          Campaign dates <span className="text-red-500">*</span>
        </span>
        <MbHelpTooltip label="About campaign dates">
          Pick a start and end date. Use presets for common flight lengths, or
          choose any custom range. Earliest start: {minStart}.
        </MbHelpTooltip>
      </div>

      <div
        className={`mb-cal-range__edges ${
          startError || endError ? 'is-invalid' : ''
        } ${open ? 'is-open' : ''}`}
      >
        <button
          type="button"
          className={`mb-cal-range__edge ${
            open && focusEdge === 'start' ? 'is-active' : ''
          }`}
          aria-expanded={open && focusEdge === 'start'}
          onClick={() => openPicker('start')}
        >
          <span className="mb-cal-range__edge-label">Starts</span>
          <span
            className={`mb-cal-range__edge-value ${
              startDate ? 'has-value' : ''
            }`}
          >
            {formatLong(startDate)}
          </span>
        </button>
        <div className="mb-cal-range__divider" aria-hidden />
        <button
          type="button"
          className={`mb-cal-range__edge ${
            open && focusEdge === 'end' ? 'is-active' : ''
          }`}
          aria-expanded={open && focusEdge === 'end'}
          onClick={() => openPicker('end')}
        >
          <span className="mb-cal-range__edge-label">Ends</span>
          <span
            className={`mb-cal-range__edge-value ${endDate ? 'has-value' : ''}`}
          >
            {formatLong(endDate)}
          </span>
        </button>
        {nights > 0 ? (
          <span className="mb-cal-range__nights">
            {nights} {nights === 1 ? 'day' : 'days'}
          </span>
        ) : null}
      </div>

      <div className="mb-cal-range__presets" role="group" aria-label="Quick ranges">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => applyPreset(p.days)}
            className={`mb-cal-range__preset ${
              activePreset === p.id ? 'is-active' : ''
            }`}
          >
            {p.label}
          </button>
        ))}
        {(startDate || endDate) && (
          <button
            type="button"
            className="mb-cal-range__preset mb-cal-range__preset--ghost"
            onClick={() => {
              clearError?.('startDate');
              clearError?.('endDate');
              onChange({ startDate: '', endDate: '' });
              setFocusEdge('start');
              setOpen(true);
            }}
          >
            Clear
          </button>
        )}
      </div>

      <FloatingOverlay
        open={open}
        onClose={closePicker}
        title="Campaign dates"
        subtitle={overlayHint}
        maxWidth={640}
      >
        <div className="mb-cal-overlay-body">
          <div className="mb-cal-inline__toolbar mb-cal-overlay-toolbar">
            <div className="mb-cal-range__presets mb-cal-overlay-presets" role="group">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.days)}
                  className={`mb-cal-range__preset ${
                    activePreset === p.id ? 'is-active' : ''
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mb-cal-inline__nav">
              <button
                type="button"
                aria-label="Previous month"
                className="mb-cal-inline__nav-btn"
                onClick={() => setCursor((c) => addMonths(c, -1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Next month"
                className="mb-cal-inline__nav-btn"
                onClick={() => setCursor((c) => addMonths(c, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-cal-inline__months px-3 pb-1 sm:px-4">
            {renderMonth(monthLeft, cellsLeft, 'L')}
            {renderMonth(monthRight, cellsRight, 'R')}
          </div>

          <div className="mb-cal-inline__footer mb-cal-overlay-footer">
            <span className="mb-cal-inline__footer-note">
              {startDate && endDate
                ? `${formatLong(startDate)} → ${formatLong(endDate)} · ${nights} ${nights === 1 ? 'day' : 'days'}`
                : `Earliest ${minStart}`}
            </span>
            <button
              type="button"
              className="mb-cal-inline__done"
              disabled={!startDate || !endDate}
              onClick={closePicker}
            >
              Done
            </button>
          </div>
        </div>
      </FloatingOverlay>

      <FieldError message={startError || endError} />
    </div>
  );
}
